import { Box, Divider, Group, Slider, Text } from '@byecode/ui'
import type { TypeInstanceMap } from '@lighthouse/core'
import { type Field, QrBarcodeEnum } from '@lighthouse/core'
import {
    getInputInnerTypeMatch,
    getIsFindUseInRichText,
    INNER_TYPE_CANNOT_DISPLAY_AS_TEXT,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    PAGE_LINK_OPTION,
    useAtomData,
    useFindUseObjectContext
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { CodeTypeSelector } from './CodeTypeSelector'

const SCxContainer = styled.div`
    height: 100%;
`

const FormItemContent = styled.div`
    flex-shrink: 0;
    width: 180px;
`

interface QrBarcodeSettingProps {}

const filterField = (arg: Field | TypeInstanceMap) => !INNER_TYPE_CANNOT_DISPLAY_AS_TEXT.has(typeof arg === 'object' ? arg.innerType : arg)

export const QrBarcodeSetting: React.FC<QrBarcodeSettingProps> = () => {
    const styleProps = getBlockAndPageDesignLimit('qrBarcode')
    const disabledWithVersion = useIsDisabledWithVersion()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const { control } = useFormContext()
    const findUseObject = useFindUseObjectContext()
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const codeType = useWatch({ control, name: 'codeType' })

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()

    const contentLabel = useMemo(() => (codeType === QrBarcodeEnum.BARCODE ? '条形码内容' : '二维码内容'), [codeType])

    return (
        <Box style={{ height: '100%' }}>
            <BlockConfigureSegmentedControl isInterAction value={currentTab} onChange={setCurrentTab} />

            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN}>
                <PositionSetting {...styleProps.position} />
                <Divider />
                <SizeSetting {...styleProps.size} />
                <Divider />
                <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
            </SCxContainer>
            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE}>
                <Group label="类型">
                    <ListItem4ByecodeUi grow>
                        <Controller name="codeType" control={control} render={({ field }) => <CodeTypeSelector {...field} />} />
                    </ListItem4ByecodeUi>
                </Group>
                <Divider color="var(--color-gray-200)" />
                <Group label="内容">
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>{contentLabel}</Text>
                        <FormItemContent>
                            <Controller
                                control={control}
                                name="content"
                                render={({ field }) => {
                                    const isHighLight = getIsFindUseInRichText({
                                        doc: field.value,
                                        findUseObject
                                    })
                                    return (
                                        <NodeFieldPreview
                                            innerTypes={getInputInnerTypeMatch('TEXT')}
                                            disabled={disabledWithVersion}
                                            highlighting={isHighLight}
                                            style={{ width: '100%' }}
                                            autoHeight
                                            placeholder="请输入"
                                            viewOptions={customViewOption ? [customViewOption] : undefined}
                                            pageOptions={[PAGE_LINK_OPTION, ...pageOptions]}
                                            userOption={userOption}
                                            value={field.value}
                                            dataSourceList={dataSourceList}
                                            onChange={field.onChange}
                                            onBlur={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </FormItemContent>
                    </ListItem4ByecodeUi>
                    {codeType === QrBarcodeEnum.BARCODE && (
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>柱状条宽度</Text>
                            <FormItemContent>
                                <Controller
                                    control={control}
                                    name="barWidth"
                                    render={({ field }) => (
                                        <Slider
                                            min={1}
                                            max={5}
                                            step={1}
                                            {...field}
                                            style={{ width: 180 }}
                                            inputProps={{
                                                onFocus: e => {
                                                    e.target.select()
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </FormItemContent>
                        </ListItem4ByecodeUi>
                    )}
                </Group>

                <Divider />

                <AnchorSetting />
                <Divider />
                <VisibilitySetting />
            </SCxContainer>

            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION}>
                <Controller
                    name="eventList"
                    control={control}
                    render={({ field }) => <EventList blockType="qrBarcode" value={field.value || []} onChange={field.onChange} />}
                />
                <Divider />
                <AnimationSetting />
            </SCxContainer>
        </Box>
    )
}
